<template>
    <AuthLayout>
        <div class="row">
            <div class="col-xl-4 order-xl-2 mb-4">
                <div class="text-center">
                    <div class="d-inline-flex" id="userAvatarWrap" @click="triggerFileInput">
                        <img id="userAvatar" :src="userPicture" class="object-fit-contain rounded-circle mx-auto border-3 border cursor-pointer" :alt="user.name + ' avatar'" width="150" height="150">
                    </div>
                    <h5 class="ff-dsb mt-3 mb-0">{{ user.name }}</h5>
                    <div class="fs-15">{{ user.is_agent ? 'Agent' : 'Customer' }}</div>
                    <div class="bg-white shadow-sm p-4 rounded-3 mt-3 text-start">
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item d-flex justify-content-between align-items-start px-0">
                                <div class="me-auto">
                                    <div class="ff-dsb">Email</div>
                                </div>
                                {{ user.email }}
                            </li>
                            <li class="list-group-item d-flex justify-content-between align-items-start px-0">
                                <div class="me-auto">
                                    <div class="ff-dsb">Created</div>
                                </div>
                                {{ user.created }}
                            </li>
                            <li class="list-group-item d-flex justify-content-between align-items-start px-0">
                                <div class="me-auto">
                                    <div class="ff-dsb">Updated</div>
                                </div>
                                {{ user.updated }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="col-xl-8 vstack gap-4">
                <div class="bg-white shadow-sm p-4 rounded-3">
                    <form @submit.prevent="updateProfile" class="vstack gap-4">
                        <div v-if="honeypot.enabled" :name="`${honeypot.nameFieldName}_wrap`" style="display:none;">
                            <input type="text" v-model="updateProfileForm.nameFieldName" :name="honeypot.nameFieldName" />
                            <input type="text" v-model="updateProfileForm.validFromFieldName" :name="honeypot.validFromFieldName" />
                        </div>
                        <h5 class="ff-dsb">Edit Profile</h5>
                        <input @change="onFileChange" ref="fileInput" id="userAvatarUpload"
                            @input="updateProfileForm.avatar = $event.target.files[0]" type="file" accept="image/*"
                            class="form-control pe-none visually-hidden">
                        <Fields :fields="updateProfileForm" :register="false" />
                        <div class="hstack gap-3 justify-content-center">
                            <button class="btn-gradient-primary rounded-3 btn py-2 fs-15 px-5" type="submit" :disabled="updateProfileForm.processing">
                                Update Profile <i v-if="updateProfileForm.processing" class="fad fa-spinner-third fa-spin"></i>
                            </button>
                        </div>
                    </form>
                </div>
                <div class="bg-white shadow-sm p-4 rounded-3">
                    <form @submit.prevent="updatePassword" class="vstack gap-4" autocomplete="off">
                        <div v-if="honeypot.enabled" :name="`${honeypot.nameFieldName}_wrap`" style="display:none;">
                            <input type="text" v-model="updatePasswordForm.nameFieldName" :name="honeypot.nameFieldName" />
                            <input type="text" v-model="updatePasswordForm.validFromFieldName" :name="honeypot.validFromFieldName" />
                        </div>
                        <h5 class="ff-dsb">Change Password</h5>
                        <div class="row g-4">
                            <div class="col-12">
                                <label for="inputOldPassword" class="form-label">Current Password <sup class="text-danger">*</sup></label>
                                <div class="input-group">
                                    <input :type="showPassword ? 'text':'password'" v-model="updatePasswordForm.old_password" class="form-control" :class="errors.old_password ? 'is-invalid':''" id="inputOldPassword" placeholder="enter password" autocomplete="current-password">
                                    <button type="button" class="input-group-text rounded-end" @click="showPassword = !showPassword">
                                        <i class="fas fa-sm" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                                    </button>
                                    <div v-if="errors.old_password" class="invalid-feedback">
                                        {{ errors.old_password }}
                                    </div>
                                </div>
                            </div>
                            <div class="col-lg-6">
                                <label for="inputNewPassword" class="form-label">New Password <sup class="text-danger">*</sup></label>
                                <div class="input-group">
                                    <input :type="showPassword ? 'text':'password'" v-model="updatePasswordForm.password" class="form-control" :class="errors.password ? 'is-invalid':''" id="inputNewPassword" placeholder="enter password" autocomplete="new-password">
                                    <button type="button" class="input-group-text rounded-end" @click="showPassword = !showPassword">
                                        <i class="fas fa-sm" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                                    </button>
                                    <div v-if="errors.password" class="invalid-feedback">
                                        {{ errors.password }}
                                    </div>
                                </div>
                            </div>
                            <div class="col-lg-6">
                                <label for="inputConfirmNewPassword" class="form-label">Confirm New Password <sup class="text-danger">*</sup></label>
                                <div class="input-group">
                                    <input :type="showPassword ? 'text':'password'" v-model="updatePasswordForm.password_confirmation" class="form-control" :class="errors.password_confirmation ? 'is-invalid':''" id="inputConfirmNewPassword" placeholder="enter password" autocomplete="confirm-new-password">
                                    <button type="button" class="input-group-text rounded-end" @click="showPassword = !showPassword">
                                        <i class="fas fa-sm" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                                    </button>
                                    <div v-if="errors.password_confirmation" class="invalid-feedback">
                                        {{ errors.password_confirmation }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="hstack gap-3 justify-content-center">
                            <button class="btn-gradient-primary rounded-3 btn py-2 fs-15 px-5" type="submit" :disabled="updatePasswordForm.processing">
                                Change Password <i v-if="updatePasswordForm.processing" class="fad fa-spinner-third fa-spin"></i>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </AuthLayout>
</template>

<script setup>
import { useReCaptcha } from 'vue-recaptcha-v3';
import AuthLayout from '../../../../../../../resources/js/front/Layouts/AuthLayout.vue';
import Fields from '../../Components/Fields.vue';
import { useForm, Link } from '@inertiajs/vue3';
import { ref } from 'vue';

const props = defineProps({ errors: Object, user: Object, honeypot: Object })
const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();
const showPassword = ref(false);

const updateProfileForm = useForm({
    name: props.user.name ?? null,
    email: props.user.email ?? null,
    contact_no: props.user.contact_no ?? null,
    avatar: null,
    nameFieldName: '',
    g_recaptcha_response: null,
    validFromFieldName: props.honeypot.encryptedValidFrom,
    _method: 'PUT',
});

const updatePasswordForm = useForm({
    old_password: null,
    password: null,
    password_confirmation: null,
    nameFieldName: '',
    g_recaptcha_response: null,
    validFromFieldName: props.honeypot.encryptedValidFrom,
    _method: 'PUT',
});

const userPicture = ref(props.user.picture);
const fileInput = ref(null);

const triggerFileInput = () => {
    fileInput.value.click();
};

const onFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            userPicture.value = e.target.result;
        };
        reader.readAsDataURL(file);
    }
};

async function updateProfile() {
    await recaptchaLoaded()
    updateProfileForm.g_recaptcha_response = await executeRecaptcha('updateProfile')
    updateProfileForm.post(route('user.profile.update'), {
        //for toast repeat same success props
        preserveState: 'errors'
    })
}

async function updatePassword() {
    await recaptchaLoaded()
    updatePasswordForm.g_recaptcha_response = await executeRecaptcha('updatePassword')
    updatePasswordForm.post(route('user.profile.change'), {
        preserveState: 'errors'
    })
}
</script>
